import { REQUEST_BODY_PROTECT_PREFIX, getEncryptedFieldNames } from './requestSecurityContract';

const HASH_SALT = 'buzzim-request-body';
const FALLBACK_SEED = 0x7f4a7c15;
const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

function isObjectPayload(data) {
	return Boolean(data) && Object.prototype.toString.call(data) === '[object Object]';
}

function needsRequestBodyCrypto(config) {
	if (!config || config._skipRequestBodyCrypto) {
		return false;
	}
	return getEncryptedFieldNames(config.url).length > 0;
}

function encodeUtf8(value) {
	if (typeof TextEncoder !== 'undefined') {
		return new TextEncoder().encode(String(value));
	}
	const encoded = encodeURIComponent(String(value));
	const bytes = [];
	for (let i = 0; i < encoded.length; i += 1) {
		if (encoded[i] === '%') {
			bytes.push(parseInt(encoded.slice(i + 1, i + 3), 16));
			i += 2;
			continue;
		}
		bytes.push(encoded.charCodeAt(i));
	}
	return Uint8Array.from(bytes);
}

function fnv1a32(value) {
	const bytes = encodeUtf8(value);
	let hash = FNV_OFFSET_BASIS >>> 0;
	for (let i = 0; i < bytes.length; i += 1) {
		hash ^= bytes[i];
		hash = Math.imul(hash, FNV_PRIME) >>> 0;
	}
	return hash >>> 0;
}

function nextState(state) {
	let current = state >>> 0;
	current ^= (current << 13) >>> 0;
	current >>>= 0;
	current ^= current >>> 17;
	current >>>= 0;
	current ^= (current << 5) >>> 0;
	return current >>> 0;
}

function xorBytes(bytes, seed) {
	const output = new Uint8Array(bytes.length);
	let state = seed >>> 0 || FALLBACK_SEED;
	for (let i = 0; i < bytes.length; i += 1) {
		state = nextState(state);
		output[i] = bytes[i] ^ (state & 0xff);
	}
	return output;
}

function bytesToBase64Url(bytes) {
	let binary = '';
	for (let i = 0; i < bytes.length; i += 1) {
		binary += String.fromCharCode(bytes[i]);
	}
	return window.btoa(binary)
		.replace(/\+/g, '-')
		.replace(/\//g, '_')
		.replace(/=+$/g, '');
}

function normalizeFieldName(fieldName) {
	return typeof fieldName === 'string' ? fieldName : '';
}

function buildSeed(fieldName, payloadLength) {
	return fnv1a32(`${HASH_SALT}|seed|${normalizeFieldName(fieldName)}|${payloadLength}`);
}

function buildChecksum(fieldName, plainText, payloadLength) {
	return fnv1a32(`${HASH_SALT}|checksum|${normalizeFieldName(fieldName)}|${payloadLength}|${plainText}`)
		.toString(16)
		.padStart(8, '0');
}

function protectFieldValue(fieldName, plainText) {
	const plainBytes = encodeUtf8(plainText);
	const maskedBytes = xorBytes(plainBytes, buildSeed(fieldName, plainBytes.length));
	const checksum = buildChecksum(fieldName, plainText, plainBytes.length);
	return `${REQUEST_BODY_PROTECT_PREFIX}.${checksum}.${bytesToBase64Url(maskedBytes)}`;
}

export async function protectRequestBody(config) {
	if (!needsRequestBodyCrypto(config) || config._requestBodyCryptoApplied) {
		return config;
	}
	if (!isObjectPayload(config.data)) {
		return config;
	}
	const fieldNames = getEncryptedFieldNames(config.url);
	const nextData = { ...config.data };
	for (const fieldName of fieldNames) {
		if (typeof nextData[fieldName] !== 'string' || !nextData[fieldName]) {
			continue;
		}
		nextData[fieldName] = protectFieldValue(fieldName, nextData[fieldName]);
	}
	config.data = nextData;
	config._requestBodyCryptoApplied = true;
	return config;
}
